import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { SchemaInjector } from '@/components/seo/SchemaInjector';
import { Mail, MessageSquare, Phone, MapPin, ArrowRight, Calendar } from 'lucide-react';
import {
  SectionBadge,
  GlowOrbBackground,
  SectionDivider,
  ProductShowcase,
} from '@/components/marketing';
import { PRODUCT_FRAMES } from '@/lib/productFrames';
import { useSanityPage } from '@/lib/sanity.hooks';
/** Render contact options and CMS-provided search metadata. */
export const ContactPage = () => {
  const { data: cmsData } = useSanityPage('contact');

  const pageTitle = cmsData?.seo?.metaTitle || 'Contact Rooflyzer OS — Get Support or a Demo';
  const pageDescription =
    cmsData?.seo?.metaDescription ||
    'Reach the Rooflyzer team for support, onboarding help, or to schedule a personalized demo of the platform.';
  const heroTitle = cmsData?.title || "Let's Talk Business.";
  const heroSubtitle =
    cmsData?.description ||
    'Whether you have questions about features, pricing, or need a personalized demo, our team is ready to help.';

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl="/contact"
        keywords="contact rooflyzer, rooflyzer support, rooflyzer demo"
        ogImage={cmsData?.seo?.ogImage?.asset?.url || '/social-card.svg'}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Contact',
              item: 'https://rooflyzer.pro/contact',
            },
          ],
        }}
      />
      <SchemaInjector type="organization" />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <GlowOrbBackground
          orbs={[
            { color: 'purple', size: 500, top: '-150px', left: '-100px', opacity: 0.14 },
            { color: 'gold', size: 300, top: '80px', right: '-50px', opacity: 0.1 },
          ]}
        />
        <div className="relative z-10 max-w-xl mx-auto">
          <SectionBadge icon={MessageSquare} text="Get In Touch" color="purple" className="mb-6" />
          <h1 className="hero-h1 font-bold text-white mb-5">
            {cmsData?.title ? heroTitle : <>Let's Talk Business.</>}
          </h1>
          <p className="hero-lead text-white/60">{heroSubtitle}</p>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4 mb-16" />

      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-emerald-400" />
              </div>
              <h2 className="section-h3 font-bold text-white">General Inquiry</h2>
            </div>
            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const firstName = formData.get('firstName') as string;
                const lastName = formData.get('lastName') as string;
                const email = formData.get('email') as string;
                const company = formData.get('company') as string;
                const message = formData.get('message') as string;

                const subject = encodeURIComponent(
                  `Contact Form: ${firstName} ${lastName} from ${company || 'N/A'}`
                );
                const body = encodeURIComponent(
                  `Name: ${firstName} ${lastName}\nEmail: ${email}\nCompany: ${company || 'N/A'}\n\nMessage:\n${message}`
                );

                window.location.href = `mailto:support@rooflyzer.pro?subject=${subject}&body=${body}`;
              }}
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-xs font-medium text-white/50 mb-1.5"
                  >
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    required
                    className="w-full bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-white/20 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-xs font-medium text-white/50 mb-1.5"
                  >
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    required
                    className="w-full bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-white/20 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="contactEmail"
                  className="block text-xs font-medium text-white/50 mb-1.5"
                >
                  Work Email
                </label>
                <input
                  id="contactEmail"
                  type="email"
                  name="email"
                  required
                  className="w-full bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-white/20 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                  placeholder="john@company.com"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-xs font-medium text-white/50 mb-1.5">
                  Company Name
                </label>
                <input
                  id="company"
                  type="text"
                  name="company"
                  className="w-full bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-white/20 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                  placeholder="Roofing & Co."
                />
              </div>
              <div>
                <label
                  htmlFor="contactMessage"
                  className="block text-xs font-medium text-white/50 mb-1.5"
                >
                  Message
                </label>
                <textarea
                  id="contactMessage"
                  rows={4}
                  name="message"
                  required
                  className="w-full bg-slate-900/80 border border-slate-800 rounded-xl px-4 py-2.5 text-white placeholder-white/20 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                  placeholder="Tell us about your needs..."
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
              >
                Send Message
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>

          {/* Schedule + Contact Info */}
          <div className="space-y-6">
            {/* Demo Scheduling */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-emerald-400" />
                </div>
                <h2 className="section-h3 font-bold text-white">Schedule a Demo</h2>
              </div>
              <p className="text-white/55 text-sm mb-5">
                Book a personalized 30-minute demo to see how Rooflyzer OS can transform your
                roofing business.
              </p>
              <Button
                onClick={() => (window.location.href = '/demo')}
                size="lg"
                className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
              >
                <Calendar className="mr-2 w-4 h-4" />
                Schedule Demo
              </Button>
            </div>

            {/* Contact Info Cards */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-3">
                  <Mail className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">Email Support</h3>
                <a
                  href="mailto:support@rooflyzer.pro"
                  className="text-emerald-400 text-sm hover:underline"
                >
                  support@rooflyzer.pro
                </a>
                <p className="text-white/40 text-xs mt-1">Demos, pricing & support</p>
              </div>

              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-3">
                  <Phone className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">Call Us</h3>
                <a href="tel:+13702898830" className="text-emerald-400 text-sm hover:underline">
                  (370) 289-8830
                </a>
                <p className="text-white/40 text-xs mt-1">Mon&ndash;Fri, 9am&ndash;6pm MT</p>
              </div>

              <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mb-3">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">Headquarters</h3>
                <address className="not-italic text-white/55 text-xs leading-relaxed">
                  30 N Gould St #55327
                  <br />
                  Sheridan, WY 82801
                </address>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summit Ridge product frame — never /screenshots/* */}
      <section className="pb-20 px-4 sm:px-6">
        <SectionDivider className="max-w-5xl mx-auto mb-16" />
        <div className="max-w-6xl mx-auto">
          <ProductShowcase frame={PRODUCT_FRAMES.dashboard} float={false} />
          <div className="mt-4 flex items-center justify-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="px-3 py-1 bg-slate-900/80 border border-slate-700/80 rounded-full text-sm text-slate-200">
              See everything in your demo
            </span>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
